/* Security dashboard + posture pages — admin/security area.
 * MFA has been fully removed from the application. This file no longer exports
 * an MfaSetupPage. */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { isDealerUser } from "../lib/roles";

// =============================================================================
// Security Dashboard
// =============================================================================
export function SecurityDashboardPage() {
  const [data, setData] = useState(null);
  const [audit, setAudit] = useState(null);
  useEffect(() => {
    api.get("/security/dashboard").then(r => setData(r.data));
    api.get("/security/audit-log?limit=30").then(r => setAudit(r.data));
  }, []);
  if (!data) return <div className="p-10 text-center text-gray-400" data-testid="security-loading">Loading security dashboard…</div>;
  const k = data.kpis || {};
  return (
    <div className="p-5" data-testid="security-dashboard">
      <h1 className="text-2xl font-bold text-[#061426]">Security Dashboard</h1>
      <p className="text-sm text-gray-500 mb-4">Active controls + recent activity. Admin/Owner only.</p>

      <div className="flex flex-wrap gap-2 mb-4">
        <Link to="/admin/security/posture" className="ssc-btn-secondary text-sm" data-testid="security-posture-link">View posture report</Link>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-testid="security-kpis">
        <Kpi label="Failed logins 24h" value={k.failed_logins_24h} accent={k.failed_logins_24h ? "red" : null} />
        <Kpi label="Disabled users" value={k.disabled_users} />
        <Kpi label="Role changes 7d" value={k.role_changes_7d} />
        <Kpi label="Exports 24h" value={k.recent_exports} accent={k.recent_exports ? "amber" : null} />
        <Kpi label="Doc views 24h" value={k.doc_views_24h} />
        <Kpi label="Checklist" value={`${(data.checklist || []).filter(c => c.status).length}/${(data.checklist || []).length}`} accent="blue" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-4">
        <Box title="Security Checklist">
          <ul className="divide-y" data-testid="security-checklist">
            {(data.checklist || []).map(c => (
              <li key={c.key} className="py-1.5 flex items-start gap-2">
                <span className={`shrink-0 ${c.status ? "text-emerald-600" : "text-gray-400"}`}>{c.status ? "✓" : "○"}</span>
                <div className="flex-1">
                  <div className="text-sm font-semibold text-[#061426]">{c.label}</div>
                  <div className="text-xs text-gray-500">{c.note}</div>
                </div>
              </li>
            ))}
          </ul>
        </Box>
      </div>

      <Box title="Audit Log (last 30)">
        <table className="w-full text-xs" data-testid="audit-log-table">
          <thead className="text-gray-500">
            <tr><th className="text-left p-1">When</th><th className="text-left p-1">User</th><th className="text-left p-1">Action</th><th className="text-left p-1">Detail</th><th className="text-left p-1">IP</th></tr>
          </thead>
          <tbody>
            {(audit?.items || []).map(a => (
              <tr key={a.id} className="border-t" data-testid={`audit-row-${a.id}`}>
                <td className="p-1 whitespace-nowrap font-mono text-gray-500">{(a.at || "").slice(0, 19).replace("T", " ")}</td>
                <td className="p-1">{a.user_name || "—"} <span className="text-gray-400">({a.user_role || "?"})</span></td>
                <td className="p-1 font-mono">{a.action}</td>
                <td className="p-1 text-gray-600">{(a.detail || "").slice(0, 60)}</td>
                <td className="p-1 font-mono text-gray-400">{a.ip || "—"}</td>
              </tr>
            ))}
            {(audit?.items || []).length === 0 && <tr><td colSpan={5} className="p-4 text-center text-gray-400">No audit entries yet.</td></tr>}
          </tbody>
        </table>
      </Box>

      <RetentionPanel />
    </div>
  );
}

function RetentionPanel() {
  const [policy, setPolicy] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/security/retention/policy").then(r => setPolicy(r.data?.policy || null)).catch(() => {});
  }, []);

  const update = (k, v) => setPolicy(p => ({ ...(p || {}), [k]: v }));

  const save = async () => {
    setBusy(true); setErr("");
    try {
      const r = await api.patch("/security/retention/policy", policy);
      setPolicy(r.data?.policy || policy);
    } catch (e) { setErr(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const dryRun = async () => {
    setBusy(true); setErr(""); setResult(null);
    try {
      const r = await api.post("/security/retention/run-now?dry_run=true");
      setResult(r.data);
    } catch (e) { setErr(e?.response?.data?.detail || "Run failed"); }
    finally { setBusy(false); }
  };

  const runNow = async () => {
    if (!window.confirm("Run retention sweep NOW (live, not dry-run)? This will archive long-cold leads and purge expired tokens.")) return;
    setBusy(true); setErr(""); setResult(null);
    try {
      const r = await api.post("/security/retention/run-now?dry_run=false");
      setResult(r.data);
    } catch (e) { setErr(e?.response?.data?.detail || "Run failed"); }
    finally { setBusy(false); }
  };

  if (!policy) return null;
  const fields = [
    ["archive_lost_after_days", "Archive lost leads after (days)"],
    ["archive_bad_after_days", "Archive bad leads after (days)"],
    ["purge_expired_upload_links_after_days", "Purge expired upload links after (days)"],
    ["purge_export_tokens_after_days", "Purge used export tokens after (days)"],
    ["purge_signed_jtis_after_days", "Purge consumed signed-link JTIs after (days)"],
  ];

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-3 mt-4" data-testid="retention-panel">
      <h3 className="text-xs uppercase tracking-wide text-gray-500 mb-2">Data Retention Policy</h3>
      <p className="text-xs text-gray-500 mb-3">
        Daily sweep at 04:15 UTC. Archives long-cold leads (sets <code>archived=true</code> — never deleted) and purges expired one-time tokens. Edit thresholds below.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-3" data-testid="retention-policy-fields">
        {fields.map(([k, label]) => (
          <label key={k} className="text-xs">
            <div className="text-gray-600 mb-0.5">{label}</div>
            <input type="number" min={0} value={policy[k] ?? 0}
              onChange={e => update(k, parseInt(e.target.value || "0", 10))}
              className="w-full border rounded px-2 py-1" data-testid={`retention-${k}`} />
          </label>
        ))}
        <label className="flex items-center gap-2 text-xs mt-2">
          <input type="checkbox" checked={!!policy.enabled} onChange={e => update("enabled", e.target.checked)}
            data-testid="retention-enabled" />
          <span>Enable automatic daily sweep</span>
        </label>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <button onClick={save} disabled={busy} className="ssc-btn-primary text-xs" data-testid="retention-save">{busy ? "Saving…" : "Save policy"}</button>
        <button onClick={dryRun} disabled={busy} className="ssc-btn-secondary text-xs" data-testid="retention-dry-run">Dry run now</button>
        <button onClick={runNow} disabled={busy} className="text-xs px-3 py-1.5 rounded border border-red-300 text-red-700 hover:bg-red-50" data-testid="retention-run-now">Run live sweep</button>
        {err && <span className="text-xs text-red-600">{err}</span>}
      </div>
      {result && (
        <pre className="text-xs bg-gray-50 border rounded p-2 mt-3 overflow-x-auto" data-testid="retention-result">
{JSON.stringify(result.counts || result, null, 2)}
        </pre>
      )}
    </div>
  );
}

function Kpi({ label, value, accent }) {
  const c = accent === "red" ? "border-red-300 bg-red-50" :
            accent === "amber" ? "border-amber-300 bg-amber-50" :
            accent === "emerald" ? "border-emerald-300 bg-emerald-50" :
            accent === "blue" ? "border-blue-300 bg-blue-50" :
            "border-gray-200 bg-white";
  return (
    <div className={`rounded-lg border ${c} p-3`}>
      <div className="text-xs text-gray-500 uppercase tracking-wide">{label}</div>
      <div className="text-xl font-bold text-[#061426]">{value ?? 0}</div>
    </div>
  );
}
function Box({ title, children }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-3">
      <h3 className="text-xs uppercase tracking-wide text-gray-500 mb-2">{title}</h3>
      {children}
    </div>
  );
}


// =============================================================================
// Security Posture — "What we protect, how" printable trust page
// =============================================================================
export function SecurityPosturePage() {
  const { user, ready } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (ready && !isDealerUser(user)) navigate("/dealer-login");
  }, [ready, user, navigate]);

  useEffect(() => {
    api.get("/security/posture")
       .then(r => setData(r.data))
       .catch(e => setErr(e?.response?.data?.detail || "Could not load posture."));
  }, []);

  if (err) return <div className="p-12 text-center text-red-600">{err}</div>;
  if (!data) return <div className="p-12 text-center text-gray-500">Loading…</div>;

  const generated = (data.generated_at || "").slice(0, 19).replace("T", " ");
  const checklist = data.checklist || [];
  const completed = checklist.filter(c => c.status === true || c.status === "ok").length;

  return (
    <div className="bg-gray-50 min-h-screen pb-24 print:bg-white" data-testid="page-security-posture">
      {/* Toolbar — hides on print */}
      <div className="bg-white border-b sticky top-0 z-10 print:hidden">
        <div className="max-w-5xl mx-auto px-6 py-3 flex items-center justify-between gap-3">
          <Link to="/admin/security" className="text-xs text-blue-600 hover:underline">← Back to dashboard</Link>
          <div className="flex items-center gap-2">
            <button
              onClick={() => window.print()}
              className="text-xs px-4 py-1.5 rounded bg-blue-600 text-white font-semibold"
              data-testid="posture-print"
            >Print / Save as PDF</button>
            <button
              onClick={() => {
                const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url; a.download = `security-posture-${(data.generated_at || "").slice(0,10)}.json`;
                document.body.appendChild(a); a.click(); a.remove();
                URL.revokeObjectURL(url);
              }}
              className="text-xs px-4 py-1.5 rounded border bg-white"
              data-testid="posture-download-json"
            >Download JSON</button>
          </div>
        </div>
      </div>

      <article className="max-w-5xl mx-auto px-8 pt-10 pb-16 bg-white shadow-sm print:shadow-none print:px-0 mt-4 print:mt-0">
        {/* Cover */}
        <header className="border-b-2 border-blue-600 pb-4 mb-8">
          <div className="text-[10px] uppercase tracking-[0.25em] text-blue-600 font-bold mb-1">Security & Compliance Posture</div>
          <h1 className="text-3xl font-black text-gray-900">What we protect, how</h1>
          <p className="text-sm text-gray-600 mt-2 max-w-2xl">
            A live snapshot of the controls protecting customer data inside the Scott Sells Chevys CRM &amp; Deal Jacket. Generated automatically — every claim below is enforced by code, not policy.
          </p>
          <div className="text-xs text-gray-500 mt-4 flex flex-wrap gap-x-6 gap-y-1">
            <span><strong>Generated:</strong> {generated} UTC</span>
            <span><strong>By:</strong> {data.generated_by}</span>
            <span><strong>Control coverage:</strong> {completed} / {checklist.length}</span>
          </div>
        </header>

        {/* KPIs */}
        <section className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-10" data-testid="posture-kpis">
          {[
            ["Total Users", data.kpis?.total_users ?? 0],
            ["Failed Logins (24h)", data.kpis?.failed_logins_24h ?? 0],
            ["Doc Views (24h)", data.kpis?.doc_views_24h ?? 0],
            ["Exports (24h)", data.kpis?.exports_24h ?? 0],
          ].map(([label, val]) => (
            <div key={label} className="border rounded p-3 print:break-inside-avoid">
              <div className="text-[10px] uppercase tracking-wide text-gray-500">{label}</div>
              <div className="text-2xl font-black text-gray-900">{val}</div>
            </div>
          ))}
        </section>

        {/* Pillars */}
        <section className="space-y-7 mb-10" data-testid="posture-pillars">
          {(data.pillars || []).map((p) => (
            <div key={p.id} className="print:break-inside-avoid" data-testid={`pillar-${p.id}`}>
              <h2 className="text-lg font-black text-gray-900 flex items-baseline gap-2 border-b pb-1 mb-3">
                <span className="text-blue-600">▸</span> {p.title}
              </h2>
              <ul className="space-y-1.5 text-sm text-gray-700">
                {(p.controls || []).map((c, i) => (
                  <li key={i} className="flex gap-2"><span className="text-emerald-600 shrink-0">✓</span><span>{c}</span></li>
                ))}
              </ul>
            </div>
          ))}
        </section>

        {/* Checklist */}
        <section className="mb-10 print:break-inside-avoid" data-testid="posture-checklist">
          <h2 className="text-lg font-black text-gray-900 border-b pb-1 mb-3">Active Control Checklist</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
            {checklist.map((c) => {
              const isOk = c.status === true || c.status === "ok";
              const isWarn = c.status === "warn";
              return (
                <div key={c.key || c.label} className="flex gap-2 items-start">
                  <span className={isOk ? "text-emerald-600" : isWarn ? "text-amber-500" : "text-gray-300"}>
                    {isOk ? "✓" : isWarn ? "⚠" : "○"}
                  </span>
                  <div>
                    <div className="font-semibold text-gray-800">{c.label}</div>
                    <div className="text-xs text-gray-500">{c.detail || c.note}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Retention + audit summary */}
        <section className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8 print:break-inside-avoid">
          <div data-testid="posture-retention">
            <h3 className="text-sm font-bold text-gray-900 border-b pb-1 mb-2">Retention Policy</h3>
            {data.retention_policy && Object.keys(data.retention_policy).length > 0 ? (
              <ul className="text-xs text-gray-700 space-y-1">
                {Object.entries(data.retention_policy).map(([k, v]) => (
                  <li key={k}><strong>{k.replace(/_/g, " ")}:</strong> {String(v)}</li>
                ))}
              </ul>
            ) : <p className="text-xs text-gray-500">Using defaults — open the Security Dashboard to tune.</p>}
          </div>
          <div>
            <h3 className="text-sm font-bold text-gray-900 border-b pb-1 mb-2">Audit Activity (last 7 days)</h3>
            <table className="text-xs w-full" data-testid="posture-audit-summary">
              <tbody>
                {(data.audit_summary_7d || []).slice(0, 12).map((a) => (
                  <tr key={a.action}><td className="font-mono py-0.5 text-gray-700">{a.action}</td><td className="text-right text-gray-900 font-bold">{a.count}</td></tr>
                ))}
                {(data.audit_summary_7d || []).length === 0 && (
                  <tr><td className="text-gray-400 italic">No activity in the last 7 days.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        {/* Footer */}
        <footer className="border-t pt-4 mt-8 text-[10px] text-gray-500 leading-relaxed">
          This document was generated by the live CRM. Controls described are enforced at the code level via FastAPI route gates, middleware, and scheduled jobs. To audit any specific claim, open the corresponding endpoint in <code>/app/backend/routes/security*.py</code>. Allow-listed origins: {(data.cors_origins || []).join(", ")}.
        </footer>
      </article>
    </div>
  );
}
